/**
 * Экран цели (27.10.2026, docs/design/goals-tab-2026-10-27) — как экран счёта
 * и кредита: тап по цели открывает её саму, а правка — кнопкой «Настроить».
 *
 * Сверху — сколько накоплено и из скольких, полоска. Ниже — «Как идёт»:
 * сколько осталось, сколько в месяц и успевает ли к сроку, сколько отложено в
 * этом месяце. Последним — взносы по цели днями, как в «Операциях».
 */
import { useState, type ReactNode } from 'react';
import { ZERO, add, bufferView, formatMoney, goalEta, kop, monthlyForDeadline, type Money, type PlainDate } from '@skoka/core';
import { skipGoalThisCycle, type AppState, type GoalEvent, type Tx } from './store.js';
import { SheetWrap } from './sheet.js';
import { flash } from './flash.js';
import { goalCycle } from './goal-cycle.js';
import { Icon } from './icon.js';
import { IconGlyph } from './account-glyph.js';
import { goalAccountId } from './goal-account.js';
import { goalMonthly } from './goal-row.js';

const MONTHS_DAT = ['январю', 'февралю', 'марту', 'апрелю', 'маю', 'июню', 'июлю', 'августу', 'сентябрю', 'октябрю', 'ноябрю', 'декабрю'];
/** «к июлю 2027». */
const byMonth = (d: PlainDate) => `к ${MONTHS_DAT[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;

export function GoalScreen({ state, goalId, needFloor, onBack, onFund, onSettings, history, backLabel = 'Цели', onChanged }: {
  state: AppState; goalId: string; needFloor: Money; backLabel?: string; onChanged?: () => Promise<void>;
  onBack: () => void; onFund: () => void; onSettings: () => void;
  /** Взносы по цели — строками «Операций», с правкой по тапу. */
  history: (txs: Tx[], allocations: GoalEvent[]) => ReactNode;
}) {
  const [confirmSkip, setConfirmSkip] = useState<PlainDate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const g = state.goals.find((x) => x.id === goalId);
  if (!g) return null;
  const cycle = goalCycle(state, g.id);
  const buffer = g.kind === 'emergency_fund';
  const pct = g.targetAmount > ZERO ? Math.min(100, Math.round((g.currentAmount / g.targetAmount) * 100)) : 0;
  const left = kop(Math.max(0, g.targetAmount - g.currentAmount));
  const account = state.accounts.find((a) => a.id === goalAccountId(state, g));
  const txs = state.transactions.filter((t) => t.link?.kind === 'goal' && t.link.goalId === g.id)
    .sort((a, b) => (b.occurredAt ?? b.occurredDate).localeCompare(a.occurredAt ?? a.occurredDate));
  const txIds = new Set(txs.map((t) => t.id));
  const allocations = (state.goalEvents ?? []).filter((e) => e.goalId === g.id
    && e.kind === 'contribution' && e.amount > ZERO && (!e.txId || !txIds.has(e.txId)));
  const entries = [
    ...txs.map((tx) => ({ date: tx.occurredDate, at: tx.occurredAt, amount: tx.amount, tx })),
    ...allocations.map((event) => ({ date: event.date, at: event.createdAt, amount: event.amount, event })),
  ].sort((a, b) => b.date.localeCompare(a.date) || b.at.localeCompare(a.at));
  const month = state.today.slice(0, 7);
  const thisMonth = add(ZERO, ...entries.filter((e) => e.date.startsWith(month)).map((e) => e.amount));
  const recent = entries.slice(0, 8);

  // Срок: успевает ли по нынешнему взносу. Нет ни взноса, ни срока — честно «не рассчитан».
  const monthly = g.monthlyContribution;
  const eta = monthly > ZERO ? goalEta(g, monthly, state.today) : null;
  let pace: { text: string; warn: boolean };
  if (g.status === 'achieved' || left === ZERO) pace = { text: 'собрано', warn: false };
  else if (g.status === 'paused') pace = { text: 'на паузе', warn: false };
  // Сумма взноса — справа; под словом «Взнос» — только к какому сроку и успевает ли.
  else if (g.targetDate && monthly <= ZERO) pace = { text: `чтобы успеть ${byMonth(g.targetDate)}`, warn: false };
  else if (g.targetDate) {
    pace = eta && eta <= g.targetDate
      ? { text: `успевает ${byMonth(g.targetDate)}`, warn: false }
      : { text: `не успевает ${byMonth(g.targetDate)} — нужно ≈ ${formatMoney(monthlyForDeadline(g, g.targetDate, state.today))}`, warn: true };
  } else pace = eta ? { text: `соберётся ${byMonth(eta)}`, warn: false } : { text: 'срок не рассчитан — назначь взнос', warn: true };

  const bv = buffer && needFloor > ZERO ? bufferView(g.currentAmount, needFloor) : null;
  const acts: [string, string, () => void, boolean?][] = [
    ['piggy-bank', 'Отложить', onFund, true], ['pen-to-square', 'Настроить', onSettings],
  ];
  return (
    <>
      <div className="obj-top">
        <button className="obj-back" onClick={onBack}><Icon name="chevron-left" size={14} />{backLabel}</button>
        <div className="obj-head"><IconGlyph icon={buffer ? 'umbrella' : 'bullseye-arrow'} color={buffer ? 'blue' : 'green'} size="s" /><b>{g.name}</b></div>
        <div className="obj-sumrow"><span className="obj-sum num">{formatMoney(g.currentAmount)}</span></div>
        <div className="obj-sub">из {formatMoney(g.targetAmount)}{account ? ` · ${account.name}` : ''}</div>
        <div className="obj-bar goal"><i style={{ width: `${pct}%` }} /></div>
        {/* Две кнопки — рядом по центру, а не по краям экрана. */}
        <div className="obj-acts pair">
          {acts.map(([icon, label, go, main]) => (
            <button key={label} className={main ? 'main' : ''} onClick={go}><span><Icon name={icon} size={19} /></span>{label}</button>
          ))}
        </div>
      </div>

      <section className="blk">
        <div className="blkhead"><b>Как идёт</b><span className="grpsum num">{pct}%</span></div>
        <div className="li"><div className="nm"><b>Осталось собрать</b></div><div className="am num">{formatMoney(left)}</div></div>
        <div className="li"><div className="nm"><b>Взнос</b><span className={pace.warn ? 'warn' : ''}>{pace.text}</span></div>
          <div className="am num">{goalMonthly(g, state.today)}</div></div>
        <div className="li"><div className="nm"><b>Отложено в этом месяце</b></div><div className="am num">{formatMoney(thisMonth)}</div></div>
        {bv && (
          <div className="li"><div className="nm"><b>Хватит на</b><span>месяц расходов — {formatMoney(needFloor)}</span></div>
            <div className="am num">{String(bv.months).replace('.', ',')} мес</div></div>
        )}
      </section>

      {cycle && <section className="blk goal-cycle">
        <div className="blkhead"><b>Взнос после зарплаты</b><span>{new Date(`${cycle.date}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}</span></div>
        {cycle.skipped ? <>
          <b className="cycle-status">Этот взнос пропущен</b>
          <p>Следующее напоминание — после следующего прихода. Накопленное и ежемесячный взнос сохранены.</p>
        </> : <>
          <div className="li"><div className="nm"><b>Ещё не отложено</b><span>можно записать другую сумму</span></div><div className="am num">{formatMoney(cycle.amount)}</div></div>
          <button className="btn ghost cycle-skip" onClick={() => { setError(null); setConfirmSkip(cycle.date); }}>Пропустить этот взнос</button>
        </>}
      </section>}
      <div className="obj-h"><b>Взносы</b></div>
      {entries.length === 0 ? <p className="obj-none">Взносов пока нет — «Отложить» запишет первый.</p>
        : history(recent.flatMap((e) => 'tx' in e ? [e.tx] : []), recent.flatMap((e) => 'event' in e ? [e.event] : []))}
      {confirmSkip && <SheetWrap title="Пропустить этот взнос?" onClose={() => { if (!busy) setConfirmSkip(null); }}>
        <p className="skip-copy">Уберём напоминание об этом взносе до следующего прихода. Накопленное и ежемесячный взнос не изменятся.</p>
        {error && <p className="note warn" role="alert">{error}</p>}
        <button className="btn pri" disabled={busy} onClick={async () => {
          setBusy(true); setError(null);
          try {
            await skipGoalThisCycle(g.id, confirmSkip);
            flash(`Взнос в «${g.name}» пропущен до следующего прихода`);
            await onChanged?.(); setConfirmSkip(null);
          } catch (e) { setError(e instanceof Error ? e.message : 'Не удалось пропустить взнос'); }
          finally { setBusy(false); }
        }}>{busy ? 'Сохраняю…' : 'Пропустить взнос'}</button>
        <button className="btn ghost" disabled={busy} onClick={() => setConfirmSkip(null)}>Отмена</button>
      </SheetWrap>}
    </>
  );
}
